import { useInfiniteQuery } from '@tanstack/react-query';
import { queryKeys, type ArticleCard, type Locale } from '@nk/shared';
import { publicApi, type CursorResult, type FeedParams } from '@/lib/api-client';
import { STALE } from '@/lib/query';

/**
 * The cursor feed behind every listing page — phases.md §2 ("cursor-based for public
 * feeds") and the Phase 10 task list.
 *
 * `useInfiniteQuery` rather than page state, for one reason that matters on a news site:
 * a reader who scrolls three pages into a category, opens a story and comes back must
 * find those three pages still there. The cache holds them; component state would not
 * survive the navigation.
 *
 * `initialPageParam: null` pairs with the server's seeded first page — see `infinite()`
 * in `apps/api/src/ssr-data.ts`.
 */
export function useFeed(locale: Locale, filters: Omit<FeedParams, 'locale' | 'cursor'>) {
  const key = queryKeys.feed(locale, {
    category: filters.category,
    district: filters.district,
    tag: filters.tag,
    author: filters.author,
  });

  const query = useInfiniteQuery({
    queryKey: key,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      publicApi.articles({
        locale,
        ...filters,
        ...(pageParam ? { cursor: pageParam } : {}),
      }),
    getNextPageParam: (lastPage: CursorResult<ArticleCard>) => lastPage.nextCursor,
    staleTime: STALE.feed,
  });

  return {
    ...query,
    articles: query.data?.pages.flatMap((page) => page.items) ?? [],
  };
}
